import styled from 'styled-components'
import { useState, useEffect } from 'react'
import { Text } from 'ui'
import { ArrowLeftIcon } from './icons'
import { Backdrop } from './SharedStyledComponents'
import { PersonalInformationProps, IssuedCode } from './types'
import BlockCouponsModal from './BlockCouponsModal'

const CODE_STATUS_CONFIG = {
    issued:   { label: 'הונפק',   bg: '#EEF9F1', border: '#16AA40', textColor: '#08832B' },
    expired:  { label: 'פג תוקף', bg: '#FFF0F0', border: '#FF4D4D', textColor: '#C0392B' },
    redeemed: { label: 'מומש',    bg: '#EEF3FF', border: '#4F87F8', textColor: '#2C5FE4' },
}

const getCodeStatus = (code: IssuedCode): keyof typeof CODE_STATUS_CONFIG => {
    const now = new Date()
    const until = new Date(code.issuedUntil)
    if (until.getTime() <= now.getTime()) return 'expired'
    return 'issued'
}

const Container = styled.div`
    display: flex;
width: 900px;
height: 902px;
/* Extra bottom padding so the last bubble doesn't sit under the desktop FloatingButtons
   (which are absolutely positioned at bottom: 20px in Coupons.tsx). */
padding: 24px 16px 150px 16px;
flex-direction: column;
align-items: center;
gap: 12px;
border-radius: 16px;
background: var(--Neutral-Surface-Light, #F6F6F6);
overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 1081px) {
    width: 100%;
    height: auto;
    padding-bottom: 48px;
  }
`

const Bubble = styled.div`
display: flex;
padding: 16px;
flex-direction: column;
justify-content: center;
gap: 10px;
align-self: stretch;
border-radius: 12px;
background: var(--Neutral-Surface-Minimal, #FFF);
position: relative;

`

const MultiSection = styled.div`
display: flex;
flex-direction: column;
align-items: flex-end;
gap: 10px;
align-self: stretch;
`

const PushBubble = styled.div<{ isLast: boolean }>`
display: flex;
padding: 12px 4px;
justify-content: space-between;
align-items: center;
align-self: stretch;
border-bottom: ${props => props.isLast ? 'none' : '1px solid var(--Neutral-Borders-Default, #D1D1D1)'};
`

const PushBubbleText = styled.div`
display: flex;
flex-direction: column;
justify-content: center;
gap: 4px;
`

const SwitchContainer = styled.div<{ isActive: boolean }>`
  position: relative;
  width: 52px;
  height: 32px;
  border-radius: 16px;
  background: ${props => props.isActive ? '#16AA40' : '#E7E7E7'};
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  padding: 2px;
  border: ${props => props.isActive ? '' : '2px solid var(--Neutral-Borders-Default, #D1D1D1)'};
`

const SwitchKnob = styled.div<{ isActive: boolean }>`
  width: ${props => props.isActive ? '24px' : '20px'};
  height: ${props => props.isActive ? '24px' : '20px'};
  border-radius: 50%;
  background: ${props => props.isActive ? '#FFFFFF' : '#9E9E9E'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, background-color 0.3s ease;
  transform: ${props => props.isActive ? 'translateX(20px)' : 'translateX(0px)'};
  position: absolute;
  left: 2px;
`

const PersonalInformation: React.FC<PersonalInformationProps> = ({
    setIsConfirmModalOpen,
    setConfirmModalTitle,
    setConfirmModalSubtitle,
    setConfirmModalConfirmButtonText,
    setConfirmModalCancelButtonText,
    setConfirmModalIsConfettiVisible,
    setConfirmModalIsGiftIconVisible,
    setConfirmModalConfirmButtonBackground,
    setConfirmModalConfirmButtonBorderColor,
    setConfirmModalConfirmButtonTextColor,
    setConfirmModalCancelButtonBackground,
    setConfirmModalCancelButtonBorderColor,
    setConfirmModalCancelButtonTextColor,
    setToastVisible,
    setToastTitle,
    setToastSubtitle,
    setBlockedMembersCount,
    customerPreferences,
    saveCustomerPreferences,
    issuedCodes,
    onViewIssuedCode,
}) => {
    const [isBlockCouponsModalOpen, setIsBlockCouponsModalOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    // Initialize toggle states from passed preferences (no blinking!)
    const [isPushNotificationsActive, setIsPushNotificationsActive] = useState(customerPreferences?.pushNotifications ?? false)
    const [isNewBenefitReminderActive, setIsNewBenefitReminderActive] = useState(customerPreferences?.newBenefitReminder ?? false)
    const [isExpiringBenefitReminderActive, setIsExpiringBenefitReminderActive] = useState(customerPreferences?.expiringBenefitReminder ?? false)

    // Update toggle states when preferences change
    useEffect(() => {
        if (customerPreferences) {
            setIsPushNotificationsActive(customerPreferences.pushNotifications ?? false)
            setIsNewBenefitReminderActive(customerPreferences.newBenefitReminder ?? false)
            setIsExpiringBenefitReminderActive(customerPreferences.expiringBenefitReminder ?? false)
        }
    }, [customerPreferences])

    const handlePushNotificationsToggle = async () => {
        const newValue = !isPushNotificationsActive
        setIsPushNotificationsActive(newValue)
        await saveCustomerPreferences({ pushNotifications: newValue })
    }

    const handleNewBenefitReminderToggle = async () => {
        const newValue = !isNewBenefitReminderActive
        setIsNewBenefitReminderActive(newValue)
        await saveCustomerPreferences({ newBenefitReminder: newValue })
    }

    const handleExpiringBenefitReminderToggle = async () => {
        const newValue = !isExpiringBenefitReminderActive
        setIsExpiringBenefitReminderActive(newValue)
        await saveCustomerPreferences({ expiringBenefitReminder: newValue })
    }


    return (
        <Container>
            <MultiSection>
                <Bubble>
                    <Text size='xl' weight='bold' textcolor='#272727'>התראות</Text>
                    <PushBubble isLast={false}>
                        <PushBubbleText>
                            <Text size='xl' weight='bold' textcolor='#272727'>הפעל התראות "פוש"</Text>
                            <Text size='xl' weight='normal' textcolor='#5D5D5D'>תזכורות גם כשהאפליקציה סגורה</Text>

                        </PushBubbleText>
                        <SwitchContainer isActive={isPushNotificationsActive} onClick={handlePushNotificationsToggle}>
                            <SwitchKnob isActive={isPushNotificationsActive} />
                        </SwitchContainer>
                    </PushBubble>
                    <PushBubble isLast={false}>
                        <PushBubbleText>
                            <Text size='xl' weight='bold' textcolor='#272727'>תזכורת על הטבה חדשה</Text>
                            <Text size='xl' weight='normal' textcolor='#5D5D5D'>קבל התראה כשמתפרסמת הטבה חדשה</Text>

                        </PushBubbleText>
                        <SwitchContainer isActive={isNewBenefitReminderActive} onClick={handleNewBenefitReminderToggle}>
                            <SwitchKnob isActive={isNewBenefitReminderActive} />
                        </SwitchContainer>
                    </PushBubble>
                    <PushBubble isLast={true}>
                        <PushBubbleText>
                            <Text size='xl' weight='bold' textcolor='#272727'>תזכורת על הטבה שפוקעת</Text>
                            <Text size='xl' weight='normal' textcolor='#5D5D5D'>קבל התראה כשהטבה עומדת לפוג</Text>

                        </PushBubbleText>
                        <SwitchContainer isActive={isExpiringBenefitReminderActive} onClick={handleExpiringBenefitReminderToggle}>
                            <SwitchKnob isActive={isExpiringBenefitReminderActive} />
                        </SwitchContainer>
                    </PushBubble>
                </Bubble>

            </MultiSection>
            {issuedCodes && issuedCodes.length > 0 && (
                <MultiSection>
                    <Bubble>
                        <Text size='xl' weight='bold' textcolor='#272727'>הקודים שלי</Text>
                        {issuedCodes.map((code, idx) => {
                            const statusKey = getCodeStatus(code)
                            const cfg = CODE_STATUS_CONFIG[statusKey]
                            return (
                                <PushBubble
                                    key={code.couponId}
                                    isLast={idx === issuedCodes.length - 1}
                                    onClick={() => onViewIssuedCode?.(code)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <Text size='xl' weight='bold' textcolor='#272727'>{code.couponTitle || code.code}</Text>
                                        <Text size='sm' weight='normal' textcolor='#888888'>קוד: {code.code}</Text>
                                    </div>
                                    <div style={{ display: 'inline-flex', padding: '4px 14px', borderRadius: 100, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                                        <Text size='sm' weight='bold' textcolor={cfg.textColor}>{cfg.label}</Text>
                                    </div>
                                </PushBubble>
                            )
                        })}
                    </Bubble>
                </MultiSection>
            )}
            <Bubble>
                <div onClick={() => setIsBlockCouponsModalOpen(true)} style={{ position: 'absolute', top: 16, left: 16, cursor: 'pointer' }}>
                    <ArrowLeftIcon />
                </div>
                <Text size='xl' weight='bold' textcolor='#272727'>חסימת קבלת הטבות</Text>
                <Text size='xl' weight='normal' textcolor='#5D5D5D'>לא מעוניינים לקבל הטבות מחברים? כנסו לבחור את מי להסיר.</Text>
            </Bubble>
            {isBlockCouponsModalOpen && <>
                <Backdrop />
                <BlockCouponsModal
                    onClose={() => setIsBlockCouponsModalOpen(false)}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    setIsConfirmModalOpen={setIsConfirmModalOpen}
                    setConfirmModalTitle={setConfirmModalTitle}
                    setConfirmModalSubtitle={setConfirmModalSubtitle}
                    setConfirmModalConfirmButtonText={setConfirmModalConfirmButtonText}
                    setConfirmModalCancelButtonText={setConfirmModalCancelButtonText}
                    setConfirmModalIsConfettiVisible={setConfirmModalIsConfettiVisible}
                    setConfirmModalIsGiftIconVisible={setConfirmModalIsGiftIconVisible}
                    setConfirmModalConfirmButtonBackground={setConfirmModalConfirmButtonBackground}
                    setConfirmModalConfirmButtonBorderColor={setConfirmModalConfirmButtonBorderColor}
                    setConfirmModalConfirmButtonTextColor={setConfirmModalConfirmButtonTextColor}
                    setConfirmModalCancelButtonBackground={setConfirmModalCancelButtonBackground}
                    setConfirmModalCancelButtonBorderColor={setConfirmModalCancelButtonBorderColor}
                    setConfirmModalCancelButtonTextColor={setConfirmModalCancelButtonTextColor}
                    setBlockedMembersCount={setBlockedMembersCount}
                />
            </>}
        </Container>
    )
}

export default PersonalInformation